/*----------------------------------------------------------------------------
    RexDateTime.ts

    All values will be UTC UNLESS the method specifically calls out local
    time
----------------------------------------------------------------------------*/

import { DateParts } from "./DateParts";

export class RexDateTime
{
    protected m_dateTimeUtc: Date;

    public constructor(msecTotal: number)
    {
        this.m_dateTimeUtc = new Date(msecTotal);
    }

    get utcValue(): number { return this.m_dateTimeUtc.valueOf(); }

    get year(): number { return this.m_dateTimeUtc.getUTCFullYear(); }

    get month(): number { return this.m_dateTimeUtc.getUTCMonth(); }

    get day(): number { return this.m_dateTimeUtc.getUTCDate(); }

    get hour(): number { return this.m_dateTimeUtc.getUTCHours(); }

    get minute(): number { return this.m_dateTimeUtc.getUTCMinutes(); }

    get second(): number { return this.m_dateTimeUtc.getUTCSeconds(); }

    get msec(): number { return this.m_dateTimeUtc.getUTCMilliseconds(); }

    toLocalTimeString(): string
    {
        return DateParts.getTimeStringFromParts(
            {
                year: this.m_dateTimeUtc.getFullYear(),
                month: 1 + this.m_dateTimeUtc.getMonth(),
                day: this.m_dateTimeUtc.getDate(),
                hour: this.m_dateTimeUtc.getHours(),
                minute: this.m_dateTimeUtc.getMinutes(),
                second: this.m_dateTimeUtc.getSeconds(),
                msec: this.m_dateTimeUtc.getMilliseconds(),
                dayOfWeek: this.m_dateTimeUtc.getDay()
            });
    }

    toLocalDateString(): string
    {
        return DateParts.getDateStringFromParts(
            {
                year: this.m_dateTimeUtc.getFullYear(),
                month: 1 + this.m_dateTimeUtc.getMonth(),
                day: this.m_dateTimeUtc.getDate(),
                dayOfWeek: this.m_dateTimeUtc.getDay()
            });
    }

    toLongDateString(): string
    {
        return DateParts.getLongDateStringFromParts(
            {
                year: this.m_dateTimeUtc.getUTCFullYear(),
                month: 1 + this.m_dateTimeUtc.getUTCMonth(),
                day: this.m_dateTimeUtc.getUTCDate(),
                dayOfWeek: this.m_dateTimeUtc.getUTCDay()
            });
    }

    toDateString(): string
    {
        return DateParts.getDateStringFromParts(
            {
                year: this.m_dateTimeUtc.getUTCFullYear(),
                month: 1 + this.m_dateTimeUtc.getUTCMonth(),
                day: this.m_dateTimeUtc.getUTCDate(),
                dayOfWeek: this.m_dateTimeUtc.getUTCDay()
            });
    }

    toLocalDateTimeString(): string
    {
        const dateParts = DateParts.getLocalDatePartsFromDate(this.m_dateTimeUtc, this.m_dateTimeUtc.getTimezoneOffset() * DateParts.s_msecPerMinute);

        return DateParts.getDateTimeStringFromParts(dateParts);
    }

    toLongLocalDateTimeString(): string
    {
        const dateParts = DateParts.getLocalDatePartsFromDate(this.m_dateTimeUtc, this.m_dateTimeUtc.getTimezoneOffset() * DateParts.s_msecPerMinute);

        return DateParts.getLongDateTimeStringFromParts(dateParts);
    }

    get isoString(): string { return this.m_dateTimeUtc.toISOString(); }

    public static createFromString(local: string): RexDateTime
    {
        const date = new Date(local);

        return new RexDateTime(date.valueOf());
    }

    /*----------------------------------------------------------------------------
        %%Function: RexDateTime.createFromParts

        parts are UTC
    ----------------------------------------------------------------------------*/
    public static createFromParts(year: number, month: number, day: number, hour?: number, minute?: number, seconds?: number, msecs?: number): RexDateTime
    {
        const date: Date = this.createDateFromOptionalParts(year, month, day, hour, minute, seconds, msecs);

        return new RexDateTime(date.valueOf());
    }

    /*----------------------------------------------------------------------------
        %%Function: RexDateTime.createFromDateParts

        parts are UTC
    ----------------------------------------------------------------------------*/
    public static createFromDateParts(parts: DateParts): RexDateTime
    {
        return this.createFromParts(parts.year, parts.month, parts.day, parts.hour, parts.minute, parts.second, parts.msec);
    }

    /*----------------------------------------------------------------------------
        %%Function: RexDateTime.createDateFromOptionalParts

        parts are UTC and the date returned is UTC
    ----------------------------------------------------------------------------*/
    public static createDateFromOptionalParts(year: number, month: number, day: number, hour?: number, minute?: number, seconds?: number, msecs?: number): Date
    {
        if (msecs)
            return new Date(Date.UTC(year, month, day, hour, minute, seconds, msecs));
        else if (seconds)
            return new Date(Date.UTC(year, month, day, hour, minute, seconds));
        else if (minute)
            return new Date(Date.UTC(year, month, day, hour, minute));
        else if (hour)
            return new Date(Date.UTC(year, month, day, hour));

        return new Date(Date.UTC(year, month, day));
    }

    /*----------------------------------------------------------------------------
        %%Function: RexDateTime.removeTime

        removes the time from this DateTime and returns only the year/month/day
    ----------------------------------------------------------------------------*/
    public removeTime(): RexDateTime
    {
        return RexDateTime.createFromParts(this.year, this.month, this.day);
    }
}